import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload,
  Visibility,
  Close,
  CheckCircle,
} from '@mui/icons-material';
import ColumnMappingStep from './ColumnMappingStep.tsx';
import PreviewStep from './PreviewStep.tsx';
import FilterStep from './FilterStep.tsx';
import readXlsxFile from 'read-excel-file';
import writeXlsxFile from 'write-excel-file';
import * as XLSX from 'xlsx';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import CommonBadges from '../../../Shared/Badges/index.jsx';

const steps = [
  'Upload Sheet',
  'Map Columns',
  'Preview & Download Sample',
  'Verify & Send',
];

const BulkTemplateUpload = ({ templateData, onClose }) => {
  const { showSnackBar } = useSnackBar();
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [sheetData, setSheetData] = useState([]);
  const [sheetColumns, setSheetColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState([]);
  const [templateParameters, setTemplateParameters] = useState([]);
  const [verifiedData, setVerifiedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [rowsWithAddedCode, setRowsWithAddedCode] = useState([]);
  const [filters, setFilters] = useState({});
  const [showInstructions, setShowInstructions] = useState(false);
  const [showRequiredParams, setShowRequiredParams] = useState(false);

  // Extract template parameters from templateData
  useEffect(() => {
    if (templateData?.components) {
      const params = [];

      params.push({
        name: 'phone_number',
        type: 'phone_number',
        required: true,
        example: '919876543210',
      });

      // params.push({
      //   name: 'assigned_to',
      //   type: 'text',
      //   required: false,
      //   example: '',
      // });

      // params.push({
      //   name: 'supplier_id',
      //   type: 'text',
      //   required: false,
      //   example: '',
      // });

      // params.push({
      //   name: 'campaign_id',
      //   type: 'text',
      //   required: false,
      //   example: '',
      // });

      const bodyComponent = templateData.components.find((c) => c.type === 'BODY');
      if (bodyComponent?.example?.body_text?.[0]) {
        bodyComponent.example.body_text[0].forEach((param, index) => {
          params.push({
            name: param,
            type: 'text',
            required: true,
            example: `Sample ${param}`,
          });
        });
      }

      const buttonComponent = templateData.components.find((c) => c.type === 'BUTTONS');
      if (buttonComponent?.buttons) {
        buttonComponent.buttons.forEach((button, index) => {
          if (button.example && button.example.length > 0) {
            params.push({
              name: `button${index + 1}`,
              type: 'url',
              required: false,
              example: button.example[0],
            });
          }
        });
      }

      setTemplateParameters(params);

      setColumnMapping(
        params.map((param) => ({
          parameter: param.name,
          sheetColumn: '',
          columnIndex: '',
          dataType: param.type,
          condition: '',
        }))
      );
    }
  }, [templateData]);

  // Validate and process phone number
  const processPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return { valid: false, processed: null, added91: false };

    const phoneStr = String(phoneNumber).replace(/\s/g, '');

    // If exactly 10 digits, add 91
    if (/^\d{10}$/.test(phoneStr)) {
      return { valid: true, processed: `91${phoneStr}`, added91: true };
    }

    // If exactly 12 digits, it's valid as is
    if (/^\d{12}$/.test(phoneStr)) {
      return { valid: true, processed: phoneStr, added91: false };
    }

    // Otherwise invalid
    return { valid: false, processed: phoneStr, added91: false };
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showSnackBar('Please upload an Excel file (.xlsx or .xls)', 'error');
      return;
    }

    const sanitizeRows = (rows) => {
      const rawHeaders = rows?.[0] || [];
      const usedHeaders = new Set();
      const keptIndices = [];
      const headers = [];
      const droppedColumns = [];

      rawHeaders.forEach((cell, index) => {
        const trimmedHeader = cell === null || cell === undefined ? '' : String(cell).trim();
        if (!trimmedHeader) {
          droppedColumns.push({ index, reason: 'Empty header' });
          return;
        }
        if (usedHeaders.has(trimmedHeader)) {
          droppedColumns.push({ index, reason: `Duplicate header "${trimmedHeader}"` });
          return;
        }
        usedHeaders.add(trimmedHeader);
        keptIndices.push(index);
        headers.push(trimmedHeader);
      });

      const dataRows = (rows || []).slice(1);
      const normalizedRows = dataRows
        .map((row) => keptIndices.map((i) => (row?.[i] !== undefined ? row[i] : null)))
        .filter((row) =>
          row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')
        );

      return { headers, normalizedRows, droppedColumns };
    };

    const parseWithXlsx = async (excelFile) => {
      const buffer = await excelFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
      const firstSheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheet];
      return XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: null,
        blankrows: false,
      });
    };

    try {
      let rows;
      let usedFallback = false;

      try {
        rows = await readXlsxFile(file);
      } catch (primaryError) {
        console.warn('Primary Excel reader failed, attempting fallback:', primaryError);
        rows = await parseWithXlsx(file);
        usedFallback = true;
      }

      if (!rows || rows.length === 0) {
        showSnackBar('The uploaded sheet is empty', 'error');
        return;
      }

      const { headers, normalizedRows, droppedColumns } = sanitizeRows(rows);

      if (headers.length === 0) {
        showSnackBar('No valid headers found. Please fix the first row and try again.', 'error');
        return;
      }

      setSheetColumns(headers);
      setSheetData(normalizedRows);
      setUploadedFile(file);

      const baseMessage = `File uploaded successfully! Found ${headers.length} columns and ${normalizedRows.length} data rows.`;
      if (droppedColumns.length > 0) {
        showSnackBar(
          `${baseMessage} Skipped ${droppedColumns.length} columns with empty/duplicate headers.`,
          'warning'
        );
      } else if (usedFallback) {
        showSnackBar(`${baseMessage} (File had some invalid cells; we ignored them.)`, 'warning');
      } else {
        showSnackBar(baseMessage, 'success');
      }

      setActiveStep(1);
    } catch (error) {
      console.error('Error reading file:', error);
      showSnackBar(`Error reading file: ${error.message}. Please ensure it's a valid Excel file.`, 'error');
    }
  };

  const getExcelColumnName = (index) => {
    let columnName = '';
    let dividend = index + 1;
    while (dividend > 0) {
      const modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = Math.floor((dividend - modulo) / 26);
    }
    return columnName;
  };

  const generateSampleFile = async () => {
    try {
      const sampleDataRows = [];

      const headerRow = columnMapping
        .filter((m) => m.sheetColumn)
        .map((m) => ({
          value: m.parameter,
          fontWeight: 'bold',
        }));
      sampleDataRows.push(headerRow);

      for (let i = 0; i < 3; i++) {
        const row = columnMapping
          .filter((m) => m.sheetColumn)
          .map((m) => {
            const param = templateParameters.find((p) => p.name === m.parameter);
            if (m.parameter === 'phone_number') {
              return { value: `919876543210` };
            }
            return { value: param?.example || `Sample ${m.parameter} ${i + 1}` };
          });
        sampleDataRows.push(row);
      }

      const readmeRows = [
        [{ value: 'Bulk Template Upload - Instructions', fontWeight: 'bold', fontSize: 14 }],
        [{ value: '' }],
        [{ value: 'IMPORTANT: Please follow these instructions carefully', fontWeight: 'bold' }],
        [{ value: '' }],
        [{ value: '1. Column Structure (Headers are Parameter Names):', fontWeight: 'bold' }],
      ];

      columnMapping
        .filter((m) => m.sheetColumn)
        .forEach((m, i) => {
          readmeRows.push([{ value: `   Column ${getExcelColumnName(i)}: ${m.parameter}` }]);
          readmeRows.push([{ value: `   Data Type: ${m.dataType}` }]);
          readmeRows.push([{ value: `   Original Sheet Column: ${m.sheetColumn}` }]);
          if (m.condition) {
            readmeRows.push([{ value: `   Condition: ${m.condition}` }]);
          }
          readmeRows.push([{ value: '' }]);
        });

      readmeRows.push(
        [{ value: '2. Phone Number Format:', fontWeight: 'bold' }],
        [{ value: '   - Can be 10 digits (91 will be added automatically)' }],
        [{ value: '   - Or 12 digits (with country code 91)' }],
        [{ value: '   - Example: 9876543210 or 919876543210' }],
        [{ value: '   - DO NOT use spaces, hyphens, or special characters' }],
        [{ value: '' }],
        [{ value: '3. Data Validation:', fontWeight: 'bold' }],
        [{ value: '   - Remove any empty rows' }],
        [{ value: '   - Ensure all required fields are filled' }],
        [{ value: '   - The column headers MUST match the parameter names exactly' }],
        [{ value: '' }],
        [{ value: '4. File Size:', fontWeight: 'bold' }],
        [{ value: '   - Maximum 3000 records per upload' }],
        [{ value: '   - Keep file size under 10MB' }],
        [{ value: '' }],
        [{ value: '5. Important Notes:', fontWeight: 'bold' }],
        [{ value: '   - Keep the header names as they are (parameter names)' }],
        [{ value: '   - Do not change column order' }],
        [{ value: '   - Fill in your data starting from row 2' }],
      );

      await writeXlsxFile([sampleDataRows, readmeRows], {
        sheets: ['Sample Data', 'README'],
        fileName: `Bulk_Template_Sample_${templateData?.name || 'Template'}.xlsx`,
      });

      showSnackBar('Sample file downloaded successfully', 'success');
    } catch (error) {
      console.error('Error generating sample file:', error);
      showSnackBar('Error generating sample file', 'error');
    }
  };

  const generatePreviewFile = async () => {
    try {
      const previewRows = [];

      const headerRow = columnMapping
        .filter((m) => m.sheetColumn)
        .map((m) => ({
          value: m.parameter,
          fontWeight: 'bold',
        }));
      previewRows.push(headerRow);

      const dataToPreview = sheetData.slice(0, 100);
      dataToPreview.forEach((row) => {
        const mappedRow = columnMapping
          .filter((m) => m.sheetColumn)
          .map((m) => {
            const colIndex = sheetColumns.indexOf(m.sheetColumn);
            const cellValue = row[colIndex];
            return {
              value: cellValue !== null && cellValue !== undefined ? cellValue : ''
            };
          });
        previewRows.push(mappedRow);
      });

      const summaryRows = [
        [{ value: 'Bulk Template Upload - Preview Summary', fontWeight: 'bold', fontSize: 14 }],
        [{ value: '' }],
        [{ value: 'Template Name:', fontWeight: 'bold' }, { value: templateData?.name || 'N/A' }],
        [{ value: 'Total Records:', fontWeight: 'bold' }, { value: sheetData.length }],
        [{ value: 'Preview Records:', fontWeight: 'bold' }, { value: Math.min(sheetData.length, 100) }],
        [{ value: '' }],
        [{ value: 'IMPORTANT: File headers use Parameter Names', fontWeight: 'bold' }],
        [{ value: 'When you upload this file, make sure column headers match parameter names exactly.', fontWeight: 'bold' }],
        [{ value: '' }],
        [{ value: 'Column Mapping:', fontWeight: 'bold' }],
        [
          { value: 'Parameter (Header Name)', fontWeight: 'bold' },
          { value: 'Original Sheet Column', fontWeight: 'bold' },
          { value: 'Column Index', fontWeight: 'bold' },
          { value: 'Data Type', fontWeight: 'bold' },
          { value: 'Condition', fontWeight: 'bold' },
        ],
      ];

      columnMapping
        .filter((m) => m.sheetColumn)
        .forEach((m) => {
          summaryRows.push([
            { value: m.parameter },
            { value: m.sheetColumn },
            { value: m.columnIndex },
            { value: m.dataType },
            { value: m.condition || 'None' },
          ]);
        });

      await writeXlsxFile([previewRows, summaryRows], {
        sheets: ['Preview', 'Summary'],
        fileName: `Bulk_Template_Preview_${templateData?.name || 'Template'}.xlsx`,
      });

      showSnackBar('Preview file downloaded successfully', 'success');
    } catch (error) {
      console.error('Error generating preview file:', error);
      showSnackBar('Error generating preview file', 'error');
    }
  };

  // Process data after mapping - validate and filter based on conditions
  const processAndValidateData = () => {
    const phoneColMapping = columnMapping.find((m) => m.parameter === 'phone_number');
    const phoneColIndex = phoneColMapping
      ? sheetColumns.indexOf(phoneColMapping.sheetColumn)
      : -1;

    const validRowsData = [];
    const invalidRowsData = [];
    const rowsWith91Added = [];

    sheetData.forEach((row, index) => {
      const rowErrors = [];

      // ─── Check each mapped column's condition ───────────────────────
      columnMapping.forEach((mapping) => {
        if (!mapping.sheetColumn) return; // unmapped column, skip

        const colIndex = sheetColumns.indexOf(mapping.sheetColumn);
        const cellValue = row[colIndex];
        const isEmpty =
          cellValue === null ||
          cellValue === undefined ||
          String(cellValue).trim() === '';

        const condition = mapping.condition;

        // 'Required' or 'Not Empty' → blank values filter out
        if (condition === 'Required' || condition === 'Not Empty') {
          if (isEmpty) {
            rowErrors.push(`"${mapping.parameter}" is empty (required)`);
          }
        }

        // Phone number: handled separately below, skip here
        // URL optional: no error
      });

      // ─── Phone Number Validation ─────────────────────────────────────
      if (phoneColIndex >= 0) {
        const phoneNumber = row[phoneColIndex];
        const result = processPhoneNumber(phoneNumber);

        if (!result.valid) {
          const phoneStr = phoneNumber
            ? String(phoneNumber).replace(/\s/g, '')
            : '';
          rowErrors.push(
            !phoneNumber
              ? 'Phone number is missing'
              : phoneStr.length < 10
                ? `Phone number has only ${phoneStr.length} digits (need 10 or 12)`
                : `Phone number has ${phoneStr.length} digits (need 10 or 12)`
          );
        }

        if (rowErrors.length === 0) {
          // Row is valid — update phone number in row
          const updatedRow = [...row];
          updatedRow[phoneColIndex] = result.processed;

          validRowsData.push(updatedRow);

          if (result.added91) {
            rowsWith91Added.push(validRowsData.length - 1);
          }
        } else {
          invalidRowsData.push({
            rowNumber: index + 2,
            row: row,
            phoneNumber: row[phoneColIndex],
            reason: rowErrors.join(' | '),
          });
        }
      } else {
        // No phone column mapped
        if (rowErrors.length === 0) {
          validRowsData.push([...row]);
        } else {
          invalidRowsData.push({
            rowNumber: index + 2,
            row: row,
            phoneNumber: null,
            reason: rowErrors.join(' | '),
          });
        }
      }
    });

    // ─── Guards ──────────────────────────────────────────────────────
    if (validRowsData.length === 0) {
      showSnackBar(
        'No valid records found after applying filters and validation.',
        'error'
      );
      return;
    }

    if (validRowsData.length > 3000) {
      showSnackBar(
        'Maximum 3000 valid records allowed. Please reduce the data.',
        'error'
      );
      return;
    }

    setVerifiedData(validRowsData);
    setFilteredData(validRowsData);
    setInvalidRows(invalidRowsData);
    setRowsWithAddedCode(rowsWith91Added);

    if (invalidRowsData.length > 0) {
      showSnackBar(
        `Validation complete: ${validRowsData.length} valid, ${invalidRowsData.length} invalid records`,
        'warning'
      );
    } else {
      showSnackBar(`All ${validRowsData.length} records are valid!`, 'success');
    }

    setActiveStep(3);
  };

  
  const handleNext = () => {

    if (activeStep === 1) {
      const unmappedColumns = columnMapping.filter((m) => !m.sheetColumn);

      if (unmappedColumns.length > 0) {
        const unmappedNames = unmappedColumns.map((m) => m.parameter).join(', ');
        showSnackBar(
          `Please map all columns before proceeding. Unmapped: ${unmappedNames}`,
          'error'
        );
        return;
      }

      const missingCondition = columnMapping.filter(
        (m) => m.sheetColumn && !m.condition
      );

      if (missingCondition.length > 0) {
        const missingNames = missingCondition.map((m) => m.parameter).join(', ');
        showSnackBar(
          `Please set condition for all mapped columns: ${missingNames}`,
          'error'
        );
        return;
      }
    }

    if (activeStep === 2) {
      processAndValidateData();
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const downloadSampleFileFromStep0 = async () => {
  try {
    const sampleDataRows = [];

    // Get only required parameters
    const requiredParams = templateParameters.filter(p => p.required);
    
    const headerRow = requiredParams.map((param) => ({
      value: param.name,
      fontWeight: 'bold',
    }));
    sampleDataRows.push(headerRow);

    for (let i = 0; i < 3; i++) {
      const row = requiredParams.map((param) => {
        if (param.name === 'phone_number') {
          return { value: `919876543210` };
        }
        return { value: param.example || `Sample ${param.name} ${i + 1}` };
      });
      sampleDataRows.push(row);
    }

    const readmeRows = [
      [{ value: 'Bulk Template Upload - Instructions', fontWeight: 'bold', fontSize: 14 }],
      [{ value: '' }],
      [{ value: 'IMPORTANT: Please follow these instructions carefully', fontWeight: 'bold' }],
      [{ value: '' }],
      [{ value: '1. Required Parameters (Must be present in your sheet):', fontWeight: 'bold' }],
    ];

    requiredParams.forEach((param, i) => {
      readmeRows.push([{ value: `   - ${param.name} (${param.type})` }]);
      readmeRows.push([{ value: `     Example: ${param.example || `Sample ${param.name}`}` }]);
      readmeRows.push([{ value: '' }]);
    });

    readmeRows.push(
      [{ value: '2. Phone Number Format:', fontWeight: 'bold' }],
      [{ value: '   - Can be 10 digits (91 will be added automatically)' }],
      [{ value: '   - Or 12 digits (with country code 91)' }],
      [{ value: '   - Example: 9876543210 or 919876543210' }],
      [{ value: '   - DO NOT use spaces, hyphens, or special characters' }],
      [{ value: '' }],
      [{ value: '3. File Requirements:', fontWeight: 'bold' }],
      [{ value: '   - Maximum 3000 records per upload' }],
      [{ value: '   - Keep file size under 10MB' }],
      [{ value: '   - Remove any empty rows' }],
      [{ value: '' }],
      [{ value: '4. Important Notes:', fontWeight: 'bold' }],
      [{ value: '   - The column headers MUST match the parameter names exactly' }],
      [{ value: '   - All required fields must be filled' }],
      [{ value: '   - Do not change the header names' }],
    );

    await writeXlsxFile([sampleDataRows, readmeRows], {
      sheets: ['Sample Data', 'README'],
      fileName: `Bulk_Template_Sample_${templateData?.name || 'Template'}.xlsx`,
    });

    showSnackBar('Sample file downloaded successfully', 'success');
  } catch (error) {
    console.error('Error generating sample file:', error);
    showSnackBar('Error generating sample file', 'error');
  }
};

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Upload Sheet to Send Bulk Template
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'primary.50', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="body1" gutterBottom>
              <strong>Template:</strong> {templateData?.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel sheet with your data, map columns to template parameters, and send
              bulk messages to up to 3000 recipients.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => setShowInstructions(true)}
              size="small"
            >
              View Instructions
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Upload Sheet */}
      {/* {activeStep === 0 && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Upload Your Excel Sheet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload an Excel file (.xlsx or .xls) with any columns - you'll map them in the next step
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" fontWeight={600}>
              Phone Number Requirements:
            </Typography>
            <Typography variant="body2">
              • Can be 10 digits (91 will be added automatically - shown in yellow)
            </Typography>
            <Typography variant="body2">
              • Or 12 digits (with country code 91)
            </Typography>
            <Typography variant="body2">
              • Example: 9876543210 or 919876543210
            </Typography>
          </Alert>
          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id="upload-file-input"
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="upload-file-input">
            <Button variant="contained" component="span" startIcon={<CloudUpload />} size="large">
              Choose File
            </Button>
          </label>
          {uploadedFile && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>File uploaded:</strong> {uploadedFile.name}
              </Typography>
              <Typography variant="body2">
                <strong>Total rows:</strong> {sheetData.length}
              </Typography>
              <Typography variant="body2">
                <strong>Columns found:</strong> {sheetColumns.length}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {sheetColumns.join(', ')}
              </Typography>
            </Alert>
          )}
        </Paper>
      )} */}
      {activeStep === 0 && (
  <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
    <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      Upload Your Excel Sheet
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Upload an Excel file (.xlsx or .xls) with required parameters
    </Typography>

    {/* Required Parameters Card */}
    <Paper variant="outlined" sx={{ p: 2, mb: 3, textAlign: 'left', bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Required Parameters in Sheet:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {templateParameters.filter(p => p.required).map((param, idx) => (
          <CommonBadges
            key={idx}
            value={`${param.name} (${param.type})`}
            color="error"
            pill={true}
            icon={<span style={{ fontSize: '12px' }}>📌</span>}
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Your Excel file must have columns matching these parameter names exactly
      </Typography>
    </Paper>

    {/* Download Sample Button */}
    <Button
      variant="outlined"
      // startIcon={<Download />}
      onClick={downloadSampleFileFromStep0}
      sx={{ mb: 3 }}
      size="small"
    >
      Download Sample Template
    </Button>

    <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
      <Typography variant="body2" fontWeight={600}>
        Phone Number Requirements:
      </Typography>
      <Typography variant="body2">
        • Can be 10 digits (91 will be added automatically - shown in yellow)
      </Typography>
      <Typography variant="body2">
        • Or 12 digits (with country code 91)
      </Typography>
      <Typography variant="body2">
        • Example: 9876543210 or 919876543210
      </Typography>
    </Alert>
    
    <input
      accept=".xlsx,.xls"
      style={{ display: 'none' }}
      id="upload-file-input"
      type="file"
      onChange={handleFileUpload}
    />
    <label htmlFor="upload-file-input">
      <Button variant="contained" component="span" startIcon={<CloudUpload />} size="large">
        Choose File
      </Button>
    </label>
    {uploadedFile && (
      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>File uploaded:</strong> {uploadedFile.name}
        </Typography>
        <Typography variant="body2">
          <strong>Total rows:</strong> {sheetData.length}
        </Typography>
        <Typography variant="body2">
          <strong>Columns found:</strong> {sheetColumns.length}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          {sheetColumns.join(', ')}
        </Typography>
      </Alert>
    )}
  </Paper>
)}

      {/* Step 1: Map Columns */}
      {activeStep === 1 && (
        <ColumnMappingStep
          templateParameters={templateParameters}
          sheetColumns={sheetColumns}
          columnMapping={columnMapping}
          setColumnMapping={setColumnMapping}
          getExcelColumnName={getExcelColumnName}
          sheetData={sheetData}
        />
      )}

      {/* Step 2: Preview & Download Sample */}
      {activeStep === 2 && (
        <PreviewStep
          columnMapping={columnMapping}
          sheetData={sheetData}
          sheetColumns={sheetColumns}
          generateSampleFile={generateSampleFile}
          generatePreviewFile={generatePreviewFile}
        />
      )}

      {/* Step 3: Verify & Send */}
      {activeStep === 3 && (
        <FilterStep
          uploadedFile={uploadedFile}
          verifiedData={verifiedData}
          filteredData={filteredData}
          setFilteredData={setFilteredData}
          columnMapping={columnMapping}
          sheetColumns={sheetColumns}
          filters={filters}
          setFilters={setFilters}
          templateData={templateData}
          invalidRows={invalidRows}
          rowsWithAddedCode={rowsWithAddedCode}
        />
      )}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        <Box>
          {activeStep < 3 && (
            <Button variant="contained" onClick={handleNext}>
              {activeStep === 2 ? 'Verify & Continue' : 'Next'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Instructions Dialog */}
      <Dialog
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk Template Upload Instructions</DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>
            Step-by-Step Guide
          </Typography>
          <ol style={{ paddingLeft: 20 }}>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Upload Your Sheet:</strong> Upload any Excel file with any columns.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Map Columns:</strong> Match template parameters to your sheet columns and set conditions.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Download Sample:</strong> Download a sample file with dummy data.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Verify & Send:</strong> Review validated data and send to recipients.
              </Typography>
            </li>
          </ol>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Phone Number Format:</strong> 10 digits (91 added auto) or 12 digits
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstructions(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkTemplateUpload;
